// 組織情報管理（組織マスター）連携ヘルパー — レポート出力画面の部署ソース切替（Ph.4）
// ------------------------------------------------------------------
// 「組織情報管理」PJ Ph.4 で追加。30010 系レポート出力 3 画面
// （PDF30010DownloadPage / Excel30010DownloadPage / Excel30010CompareDownloadPage）が共用する。
//
// 目的: 各画面の部署選択肢／列挙ソースを、従来の Group.department（累積された全部署）から
//       「そのシーズン開始日に有効だった確定組織表」の部署一覧へ切り替え、廃止済み部署が
//       選択肢に残る問題を解消する。確定組織表が無いグループは Group.department へフォールバック。
//
// 最小変更の要点: 既存画面のツリー構築（genTree/genTree2）・列挙・フィルタ実行部は
//   すべて「'／' 連結の経路文字列配列」を入力とする。本ヘルパーは
//   「組織マスターの units → 経路文字列配列」への変換のみを担い、既存関数へそのまま渡す
//   （ツリービルダー・フィルタ実行部は一切変更しない）。
import Axios from 'axios';

// 階層区切り文字（Group.department と同一）
const PATH_SEP = '／';

// 所属の最大階層数（User.department〜department7 / OrgUnit.level の上限と一致）
export const MAX_DEPT_LEVEL = 7;

/**
 * 部署選択 `<TreeSelect>` の共用 props（2026-09）。
 * 5 画面（PDF10010Company / PDF30010 / PDF40010_SS / PDF60020_SS / E3DepMgr）で同じ設定を使う。
 * - listHeight: ポップアップ内リストの高さ。antd 既定 256px では 7 階層を展開すると狭いため 480px に拡大
 * - popupMatchSelectWidth: false … ポップアップ幅を選択ボックス幅に固定せず内容幅に広げる
 *   （下限は選択ボックス幅。長い経路名が折り返し・省略されない）
 * ※ 数値は antd の数値 prop（CSS 変数を受け付けない）のため直接指定。design-system.md「例外」節に記録
 */
export const DEPT_TREE_LIST_HEIGHT = 480;
export const DEPT_TREE_SELECT_PROPS = {
  treeLine: true,
  listHeight: DEPT_TREE_LIST_HEIGHT,
  popupMatchSelectWidth: false,
};

/**
 * 指定グループ・指定日時点の有効な確定組織表を取得する。
 * GET /api/org/tree/:groupCode?date=YYYY-MM-DD の応答をそのまま返す。
 * 例外時・有効バージョン無し時は { version: null, units: [] }（フォールバック安全）。
 *
 * @param {string} groupCode グループコード
 * @param {string} dateStr   基準日 'YYYY-MM-DD'（省略時はサーバー側で当日）
 * @returns {Promise<{ version: Object|null, units: Array }>}
 */
export const fetchOrgTreeAt = async (groupCode, dateStr) => {
  try {
    if (!groupCode) return { version: null, units: [] };
    const query = dateStr ? `?date=${encodeURIComponent(dateStr)}` : '';
    const res = await Axios.get(`/api/org/tree/${groupCode}${query}`);
    if (res && res.data && res.data.success) {
      return {
        version: res.data.version || null,
        units: Array.isArray(res.data.units) ? res.data.units : [],
      };
    }
    return { version: null, units: [] };
  } catch (error) {
    // 取得失敗時は組織表なし扱い（呼び出し側は Group.department へフォールバック）
    return { version: null, units: [] };
  }
};

/**
 * 組織マスターの units（GET /tree の応答）を、Group.department と同形状の
 * 「'／' 連結の経路文字列配列」へ変換する。
 *
 * - status === 'active' のノードのみを出力対象とする。
 * - lineage_id / parent_lineage_id（文字列化して比較）で親チェーンを辿り経路を組み立てる。
 * - level > maxLevel のノードは除外（既定 7 = MAX_DEPT_LEVEL。2026-09 に 5 → 7 へ引き上げ、
 *   ツリー構築・GSD・サーバー集計フィルタと揃えた）。
 * - 並び順: 木構造順（ルート → 子、兄弟は sort_order 昇順）でプレオーダー平坦化
 *   （Group.department と同形状。※ 各画面の列挙側で別途 sort() する箇所もあるが、
 *     ツリー構築（genTree）は経路文字列の集合として扱うため順序に依存しない）。
 * - 系列会社（level 0 ノード。「グループ会社・系列会社構造」PJ）がある組織表では:
 *   - level 0 ノードは経路に含めない（経路は所属①〜⑦ のみ＝User.department〜department7 と同形状）
 *   - options.company 指定時は、その系列会社ノード（name 一致・active）の子孫だけを出力する
 *   - 未指定時は全系列会社（active）の子孫の和集合（重複除去）。系列会社の下に無いノードは出力しない
 *   - level 0 ノードが 1 件も無い組織表は従来どおり（サーバー utils/orgReportPaths.js と同一規則）
 *
 * @param {Array} units GET /tree の units 配列
 * @param {{ maxLevel?: number, company?: string }} [options]
 * @returns {string[]} '／' 連結の経路文字列配列（active のみ）
 */
export const orgUnitsToPaths = (units, options = {}) => {
  const maxLevel =
    options.maxLevel === undefined ? MAX_DEPT_LEVEL : options.maxLevel;
  const list = Array.isArray(units) ? units : [];

  // lineage_id（文字列）→ ノード
  const byId = new Map();
  list.forEach((u) => {
    if (u && u.lineage_id !== undefined && u.lineage_id !== null) {
      byId.set(String(u.lineage_id), u);
    }
  });

  // 親 lineage_id（文字列）→ 子ノード配列。親が存在しない／空はルート扱い。
  const childrenOf = new Map();
  const roots = [];
  list.forEach((u) => {
    const pid =
      u && u.parent_lineage_id !== undefined && u.parent_lineage_id !== null
        ? String(u.parent_lineage_id)
        : '';
    if (pid && byId.has(pid)) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(u);
    } else {
      roots.push(u);
    }
  });

  // 兄弟は sort_order 昇順
  const bySortOrder = (a, b) => {
    const ao = typeof a.sort_order === 'number' ? a.sort_order : 0;
    const bo = typeof b.sort_order === 'number' ? b.sort_order : 0;
    return ao - bo;
  };
  roots.sort(bySortOrder);
  childrenOf.forEach((arr) => arr.sort(bySortOrder));

  // 系列会社（level 0）ノード
  const companyNodes = list.filter((u) => isCompanyUnit(u));
  const hasCompanies = companyNodes.length > 0;

  // ノードの経路（ルート → 当該ノード）を '／' 連結で組み立てる
  // ※ 系列会社（level 0）ノードは経路に含めない
  const pathOf = (node) => {
    const chain = [];
    let cur = node;
    const guard = new Set();
    while (cur && !guard.has(String(cur.lineage_id))) {
      guard.add(String(cur.lineage_id));
      if (!isCompanyUnit(cur)) chain.unshift(cur.name);
      cur =
        cur.parent_lineage_id !== undefined && cur.parent_lineage_id !== null
          ? byId.get(String(cur.parent_lineage_id))
          : null;
    }
    return chain.join(PATH_SEP);
  };

  const out = [];
  const visit = (node, guard) => {
    const id = String(node.lineage_id);
    if (guard.has(id)) return; // 循環参照の安全弁
    guard.add(id);
    if (!isCompanyUnit(node)) {
      const level =
        typeof node.level === 'number'
          ? node.level
          : pathOf(node).split(PATH_SEP).length;
      if (node.status === 'active' && level <= maxLevel) {
        const path = pathOf(node);
        if (path && path.trim() !== '') out.push(path);
      }
    }
    const kids = childrenOf.get(id);
    if (kids) kids.forEach((kid) => visit(kid, guard));
  };

  if (!hasCompanies) {
    // 系列会社ノードの無い組織表（従来）: ルートから全体を辿る
    roots.forEach((root) => visit(root, new Set()));
  } else {
    // 系列会社ノードのある組織表: 対象の系列会社（active）の子孫だけを辿る
    const companyName =
      options.company === undefined || options.company === null
        ? null
        : String(options.company).trim();
    const targets = companyNodes
      .filter((u) => u.status === 'active')
      .filter((u) => companyName === null || String(u.name).trim() === companyName)
      .sort(bySortOrder);
    targets.forEach((companyNode) => visit(companyNode, new Set()));
  }

  // 経路重複（系列会社をまたぐ同名経路を含む）は排除（構築順は維持）
  return Array.from(new Set(out));
};

/**
 * 系列会社（level 0）ノードか（「グループ会社・系列会社構造」PJ）。
 * サーバーは系列会社ありグループの組織表の最上位ノードに level 0 を付ける。
 */
export const isCompanyUnit = (unit) => !!unit && unit.level === 0;

/**
 * 組織マスターの units から系列会社名（active な level 0 ノードの name）を sort_order 順で返す。
 * level 0 ノードが無い組織表（系列会社なしグループ等）では空配列。
 * @param {Array} units GET /tree・GET /versions/:g/:v/units の units 配列
 * @returns {string[]} 系列会社名（重複除去・前後空白除去）
 */
export const orgUnitsToCompanyNames = (units) => {
  const list = (Array.isArray(units) ? units : []).filter(
    (u) => isCompanyUnit(u) && u.status === 'active'
  );
  list.sort((a, b) => {
    const ao = typeof a.sort_order === 'number' ? a.sort_order : 0;
    const bo = typeof b.sort_order === 'number' ? b.sort_order : 0;
    return ao - bo;
  });
  const names = list
    .map((u) => (u.name === undefined || u.name === null ? '' : String(u.name).trim()))
    .filter((name) => name !== '');
  return Array.from(new Set(names));
};

/**
 * '／' 連結の経路文字列配列から antd `<TreeSelect>` 用の treeData を組み立てる（2026-09）。
 * ------------------------------------------------------------------
 * 従来は各画面（PDF10010Company / PDF30010(+genTree2) / PDF40010_SS / PDF60020_SS / E3DepMgr）に
 * 同一の genTree が複製され、いずれも 5 階層までしか組み立てられなかった。本関数へ集約し 7 階層に対応する。
 *
 * - ノード: { value: フル経路, title: 末尾の階層名, icon?, children: [], disabled? }
 * - 親の解決は「親経路の完全一致」（経路 Map）。旧実装は `path.includes(parent.value)` の部分一致で、
 *   同じ親の下に「営業」「営業部」のような接頭辞関係の兄弟があると子が両方に重複付与されていた
 * - 親が配列に無い経路（孤児）は旧実装と同様にスキップ
 * - enabledPaths を渡すと、含まれない経路は disabled: true（30010 の role 7 所属指定 = 旧 genTree2）
 *
 * @param {string[]} paths '／' 連結の経路文字列配列（Group.department / orgUnitsToPaths の出力）
 * @param {{ icon?: any, enabledPaths?: string[], maxLevel?: number }} [options]
 * @returns {Array} treeData
 */
export const buildDepartmentTree = (paths, options = {}) => {
  const maxLevel =
    options.maxLevel === undefined ? MAX_DEPT_LEVEL : options.maxLevel;
  const enabled = Array.isArray(options.enabledPaths)
    ? new Set(options.enabledPaths)
    : null;
  const list = (Array.isArray(paths) ? paths : []).filter(
    (v) => typeof v === 'string' && v !== ''
  );
  // 浅い階層から順に親へぶら下げる（元の並び順は同一深さ内で維持）
  const byDepth = list
    .map((path, index) => ({ path, index, depth: path.split(PATH_SEP).length }))
    .filter((e) => e.depth <= maxLevel)
    .sort((a, b) => a.depth - b.depth || a.index - b.index);
  const nodeOf = new Map();
  const roots = [];
  byDepth.forEach(({ path, depth }) => {
    if (nodeOf.has(path)) return; // 重複経路は最初の 1 件のみ
    const parts = path.split(PATH_SEP);
    const node = {
      value: path,
      title: parts[depth - 1],
      children: [],
    };
    if (options.icon !== undefined) node.icon = options.icon;
    if (enabled) node.disabled = !enabled.has(path);
    if (depth === 1) {
      roots.push(node);
    } else {
      const parent = nodeOf.get(parts.slice(0, depth - 1).join(PATH_SEP));
      if (!parent) return; // 親が無い経路は表示しない
      parent.children.push(node);
    }
    nodeOf.set(path, node);
  });
  return roots;
};
